import { createContext, useContext } from 'react';
import type { Role } from '@aithora/shared';

/** The Sales page's four tabs (design salesVals slTabs, L20437). */
export const SALES_TABS = ['CRM', 'Sales Kit', 'Outcomes', 'Targeting'] as const;
export type SalesTab = (typeof SALES_TABS)[number];

/**
 * What every Sales tab gets from the page shell (SalesPage.tsx). The role and the user
 * come from the signed-in session only; the server enforces every rule again
 * (src/aithora/v3/sales.ts: Super Admin / Operator write all, Sales writes what it owns,
 * Partner reads).
 */
export interface SalesCtx {
  role: Role | null;
  /** The signed-in person's id (a deal is "mine" when its owner_id is this). */
  userId: string | null;
  /** Super Admin or Operator: reads and writes every deal. */
  isManager: boolean;
  /** May write at all (Sales, Super Admin, Operator, and not viewing as another role). */
  canWrite: boolean;
  /** Why writes are off (Partner, View as), for the disabled control's title. */
  writeReason: string | null;
  /** Switch tab; `state` carries the design's deep-link keys (e.g. { kitMode: 'lib', slDoc }). */
  setTab: (t: SalesTab, state?: Record<string, string | null>) => void;
  /** Open the meeting-prep modal (design mpOpen, T6681), for a facility or to pick one. */
  openPrep: (facilityId: string | null) => void;
  /** Jump to CRM with a deal (or a facility's deal) selected. */
  openDeal: (o: { dealId?: string; facilityId?: string }) => void;
  /** The tab's own deep-link state (URL search params other than tab). */
  params: URLSearchParams;
  setParam: (k: string, v: string | null) => void;
  /** The CRM grid is narrow (design crmNarrow): the header's Signals ticker hides. */
  crmNarrow: boolean;
  setCrmNarrow: (b: boolean) => void;
  /** Reload the header's Signals (after a write that can change them). */
  reloadSignals: () => void;
}

export const SalesContext = createContext<SalesCtx | null>(null);

export function useSales(): SalesCtx {
  const c = useContext(SalesContext);
  if (!c) throw new Error('useSales outside the Sales page');
  return c;
}
